import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/auth';
import { useRoles } from '../../hooks/useRoles';

const RoleProtectedRoute = ({ 
  children, 
  requiredRole, 
  requiredPermission,
  fallbackPath = '/dashboard',
  showUnauthorized = false 
}) => {
  const { currentUser, userProfile } = useAuth();
  const { checkRole, checkPermission } = useRoles();

  // If not authenticated, redirect to login
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // If user profile is still loading, show loading state
  if (!userProfile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading user profile...</p>
        </div>
      </div>
    );
  }

  // Check role requirement
  if (requiredRole && !checkRole(requiredRole)) {
    if (showUnauthorized) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
            <p className="text-gray-600 mb-4">
              You don't have the required role to access this page.
            </p>
            <p className="text-sm text-gray-500">
              Required role: {requiredRole}
            </p>
            <button 
              onClick={() => window.history.back()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
    return <Navigate to={fallbackPath} replace />;
  }

  // Check permission requirement
  if (requiredPermission && !checkPermission(requiredPermission)) {
    if (showUnauthorized) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
            <p className="text-gray-600 mb-4">
              You don't have the required permission to access this page.
            </p>
            <p className="text-sm text-gray-500">
              Required permission: {requiredPermission}
            </p>
            <button 
              onClick={() => window.history.back()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
    return <Navigate to={fallbackPath} replace />;
  }

  // User has required role/permission, render children
  return children;
};

export default RoleProtectedRoute; 